// Marketing tab (round-three package #6, Phase A) — the contract between
// the worker routes (`/api/admin/marketing/*`) and the client pages.
// Spec: docs/plans/feedback-round-three/marketing/SPEC.md. Money is integer
// cents ex GST everywhere; dates are Melbourne YYYY-MM-DD; months YYYY-MM.
// Every result number is calculated on the server; the client only formats.

import type { LeadSource, LeadStatus } from './types';

// ---------- Enums ----------

export const MARKETING_ACQUISITIONS = ['paid', 'organic', 'outbound'] as const;
export type MarketingAcquisition = (typeof MARKETING_ACQUISITIONS)[number];
export const MARKETING_ACQUISITION_LABELS: Record<MarketingAcquisition, string> = {
  paid: 'Paid',
  organic: 'Organic',
  outbound: 'Outbound',
};

/** Where a channel's activity numbers come from. Leads/won/revenue always
 * come from attributed OS leads, whatever the source. */
export const MARKETING_DATA_SOURCES = ['meta', 'manual', 'outreach', 'leads'] as const;
export type MarketingDataSource = (typeof MARKETING_DATA_SOURCES)[number];
export const MARKETING_DATA_SOURCE_LABELS: Record<MarketingDataSource, string> = {
  meta: 'Meta Marketing API · nightly',
  manual: 'Monthly numbers entered by hand',
  outreach: 'Live from the Outreach tab',
  leads: 'Attributed leads only',
};

export const STRATEGY_STATUSES = ['idea', 'planned', 'active', 'paused', 'ended'] as const;
export type StrategyStatus = (typeof STRATEGY_STATUSES)[number];
export const STRATEGY_STATUS_LABELS: Record<StrategyStatus, string> = {
  idea: 'Idea',
  planned: 'Planned',
  active: 'Active',
  paused: 'Paused',
  ended: 'Ended',
};

export const BUDGET_PERIODS = ['day', 'month', 'total'] as const;
export type BudgetPeriod = (typeof BUDGET_PERIODS)[number];

export const REVIEW_CADENCES = ['1w', '2w', '1m', '1q'] as const;
export type ReviewCadence = (typeof REVIEW_CADENCES)[number];
export const REVIEW_CADENCE_LABELS: Record<ReviewCadence, string> = {
  '1w': 'Every week',
  '2w': 'Every 2 weeks',
  '1m': 'Monthly',
  '1q': 'Quarterly',
};

export const TARGET_METRICS = [
  'leads_per_month',
  'cpl_max',
  'won_per_quarter',
  'revenue_per_quarter',
  'enquiries_per_month',
  'replies_per_month',
  'referrals_per_quarter',
] as const;
export type TargetMetric = (typeof TARGET_METRICS)[number];
export const TARGET_METRIC_LABELS: Record<TargetMetric, string> = {
  leads_per_month: 'Leads a month',
  cpl_max: 'Cost per lead',
  won_per_quarter: 'Jobs won a quarter',
  revenue_per_quarter: 'Won revenue a quarter',
  enquiries_per_month: 'Enquiries a month',
  replies_per_month: 'Outreach replies a month',
  referrals_per_quarter: 'Referred leads a quarter',
};
/** Target values for these metrics are integer cents. */
export const MONEY_TARGET_METRICS: readonly TargetMetric[] = ['cpl_max', 'revenue_per_quarter'];
/** Lower is better: compared against the ceiling, not paced. */
export const CEILING_TARGET_METRICS: readonly TargetMetric[] = ['cpl_max'];

export const UPDATE_KINDS = ['learning', 'change', 'result', 'review'] as const;
export type UpdateKind = (typeof UPDATE_KINDS)[number];
export const UPDATE_KIND_LABELS: Record<UpdateKind, string> = {
  learning: 'Learning',
  change: 'Change',
  result: 'Result',
  review: 'Review',
};

export const LINK_KINDS = ['creative', 'landing', 'social', 'doc'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];
export const LINK_KIND_LABELS: Record<LinkKind, string> = {
  creative: 'Creative',
  landing: 'Landing page',
  social: 'Social asset',
  doc: 'Doc',
};

/** Mirrors outreach_leads.vertical (migration 0046). */
export const OUTREACH_VERTICALS = ['healthcare', 'professional-services'] as const;
export type OutreachVertical = (typeof OUTREACH_VERTICALS)[number];
export const OUTREACH_VERTICAL_LABELS: Record<OutreachVertical, string> = {
  healthcare: 'Healthcare',
  'professional-services': 'Professional services',
};

export const LEAD_ATTRIBUTIONS = ['auto', 'manual'] as const;
export type LeadAttribution = (typeof LEAD_ATTRIBUTIONS)[number];

/** Home period tabs. month = current Melbourne calendar month (MTD);
 * quarter = the current and previous two calendar months; fy = Australian
 * financial year to date (from 1 July). */
export const MARKETING_PERIODS = ['month', 'quarter', 'fy'] as const;
export type MarketingPeriod = (typeof MARKETING_PERIODS)[number];

/** Reserved `marketing_monthly_metrics.field_key` for hand-entered spend in
 * cents (ad spend without the Meta API, referral credits, …). Every
 * strategy can record it, whatever its channel's manual_fields. */
export const SPEND_FIELD_KEY = 'spend_cents';

export const MAX_MANUAL_FIELDS = 5;
export const MAX_TARGETS = 3;

// ---------- Rows (as returned by the API; JSON columns parsed) ----------

export interface ManualField {
  key: string; // slug, unique per channel, never SPEND_FIELD_KEY
  label: string;
}

export interface MarketingChannel {
  id: string;
  name: string;
  acquisition: MarketingAcquisition;
  data_source: MarketingDataSource;
  default_lead_source: LeadSource | null;
  purpose: string | null;
  manual_fields: ManualField[];
  sort_order: number;
  created_at: number;
  updated_at: number;
}

export interface MarketingStrategy {
  id: string;
  /** Null only for ideas jotted without a channel. */
  channel_id: string | null;
  name: string;
  status: StrategyStatus;
  objective: string | null;
  idea_note: string | null;
  effort: string | null;
  start_date: string | null;
  end_date: string | null;
  budget_cents: number | null;
  budget_period: BudgetPeriod | null;
  review_every: ReviewCadence | null;
  next_review_date: string | null;
  meta_ad_set_id: string | null;
  match_keys: string[];
  outreach_vertical: OutreachVertical | null;
  created_at: number;
  updated_at: number;
}

export interface MarketingTarget {
  id: string;
  strategy_id: string;
  metric: TargetMetric;
  /** Cents for MONEY_TARGET_METRICS, otherwise a count. */
  value: number;
  sort_order: number;
}

export interface MarketingStep {
  id: string;
  strategy_id: string;
  text: string;
  done_at: number | null;
  due_date: string | null;
  sort_order: number;
}

export interface MarketingUpdate {
  id: string;
  strategy_id: string;
  date: string;
  kind: UpdateKind;
  text: string;
  created_at: number;
}

export interface MarketingLink {
  id: string;
  strategy_id: string;
  label: string;
  kind: LinkKind;
  url: string;
  social_post_id: string | null;
}

// ---------- Computed results ----------

export interface MarketingTotals {
  spend_cents: number;
  leads: number;
  won: number;
  revenue_cents: number;
  /** spend ÷ leads; null unless spend > 0 and leads > 0. */
  cpl_cents: number | null;
  /** (revenue − spend) ÷ spend; null without spend. */
  roi: number | null;
}

export interface MonthTotals extends MarketingTotals {
  meta?: MetaMetrics | null;
  month: string; // YYYY-MM
}

export interface TargetProgress {
  target: MarketingTarget;
  /** Actual for the current period; null when undefined (CPL with no spend). */
  actual: number | null;
  /** e.g. "October" or "Oct–Dec". */
  period_label: string;
  /** Count targets: actual ≥ value × elapsed fraction. Ceilings: actual ≤ value.
   * Null when actual is null. */
  on_pace: boolean | null;
  /** Count: actual ≥ value. Ceiling: actual ≤ value (and not null). */
  hit: boolean;
  /** Bar fill 0..1. Count: min(1, actual/value). Ceiling: min(1, value/actual). */
  fill: number;
}

export interface OutreachCounts {
  total: number;
  new: number;
  reached_out: number;
  followup: number;
  replied: number;
  closed: number;
  promoted: number;
  /** Promoted leads that are now won. */
  won: number;
}

export interface FunnelCounts {
  enquiry: number;
  discovery_call: number;
  proposal_sent: number;
  won: number;
}

export interface AttributedLead {
  id: string;
  name: string;
  status: LeadStatus;
  value_cents: number | null;
  enquiry_date: string | null;
  attribution: LeadAttribution;
  campaign: string | null;
}

/** Card/row shape used on home and channel pages. */
export interface StrategySummary extends MarketingStrategy {
  channel_name: string | null;
  meta_ad_set_name: string | null;
  acquisition: MarketingAcquisition | null;
  data_source: MarketingDataSource | null;
  /** 1-based days since start_date up to today (or end_date); null if not started. */
  days_running: number | null;
  targets: TargetProgress[];
  /** Current calendar month. */
  month: StrategyTotals;
  all_time: StrategyTotals;
  /** Only for data_source 'outreach' strategies with an outreach_vertical. */
  outreach: OutreachCounts | null;
}

export type AttentionKind =
  | 'over_cpl'
  | 'behind_target'
  | 'budget_pace'
  | 'review_due'
  | 'monthly_numbers_due'
  | 'unattributed'
  | 'meta_sync_failed'
  | 'meta_unmapped';

export interface AttentionItem {
  ad_set_id?: string;
  kind: AttentionKind;
  /** Pill tone hint: over_cpl → danger; behind/budget/review/monthly → gold; unattributed → neutral. */
  tone: 'danger' | 'gold' | 'neutral';
  tag: string; // "Over CPL", "Behind target", "Budget pace", "Review due", "Numbers due", "Unattributed"
  title: string; // strategy name, or "2 October leads have no strategy"
  detail: string; // "$65 a lead in October · ceiling $45"
  /** SPA path: /marketing/s/:id or /leads?… */
  href: string;
}

// ---------- Endpoint payloads ----------

/** GET /api/admin/marketing/home?period=month|quarter|fy */
export interface MarketingHomeResponse {
  meta: MetaAdsStatus;
  today: string;
  period: MarketingPeriod;
  period_label: string; // "October" · "Last 3 months" · "FY27"
  totals: MarketingTotals; // all channels, period
  /** Headline CPL is paid-only; subtitle shows blended (totals.cpl_cents). */
  by_acquisition: Record<MarketingAcquisition, MarketingTotals>;
  by_channel: { channel_id: string; name: string; totals: MarketingTotals }[];
  /** Last 6 calendar months, oldest first, current month last (MTD). */
  months: MonthTotals[];
  attention: AttentionItem[];
  channels: MarketingChannel[];
  /** status 'active', grouped by the client per channel. */
  active: StrategySummary[];
  /** status 'planned' then 'idea', newest first within each. */
  backlog: StrategySummary[];
  /** status 'ended' or 'paused'. */
  history: StrategySummary[];
}

/** GET /api/admin/marketing/channels/:id?range=month|all */
export interface MarketingChannelResponse {
  meta: MetaAdsStatus;
  unmapped_ad_sets: UnmappedAdSet[];
  today: string;
  range: 'month' | 'all';
  channel: MarketingChannel;
  totals: MarketingTotals; // range
  /** Every non-deleted strategy in the channel; `range_totals` is for the range. */
  strategies: (StrategySummary & { range_totals: StrategyTotals })[];
  /** Last 6 calendar months for this channel. */
  months: MonthTotals[];
}

/** GET /api/admin/marketing/strategies/:id */
export interface MarketingStrategyResponse {
  /** YYYY-MM months with mapped Meta rows, including zero-spend months. */
  meta_months: string[];
  meta: MetaAdsStatus;
  today: string;
  strategy: StrategySummary;
  channel: MarketingChannel | null;
  steps: MarketingStep[];
  /** Newest first. */
  updates: MarketingUpdate[];
  links: MarketingLink[];
  /** Since start (or first data), oldest first, one row per month with data or within the run. */
  months: MonthTotals[];
  funnel: FunnelCounts;
  /** Newest enquiry first, max 20; `lead_count` is the full count. */
  leads: AttributedLead[];
  lead_count: number;
  /** month → field_key → value; includes SPEND_FIELD_KEY rows (cents). */
  manual: Record<string, Record<string, number>>;
}

/** GET /api/admin/marketing/options — pickers (Lead detail, modals). */
export interface MarketingOptionsResponse {
  channels: MarketingChannel[];
  /** Non-deleted, non-idea strategies, for the Lead detail picker. */
  strategies: { id: string; name: string; status: StrategyStatus; channel_id: string | null; channel_name: string | null }[];
}

/** POST /api/admin/marketing/channels, PATCH /channels/:id (partial). */
export interface ChannelInput {
  name: string;
  acquisition: MarketingAcquisition;
  data_source: MarketingDataSource;
  default_lead_source?: LeadSource | null;
  purpose?: string | null;
  /** Labels only; the server slugs keys (stable for existing labels on PATCH). */
  manual_fields?: string[];
}

/** POST /api/admin/marketing/strategies, PATCH /strategies/:id (partial).
 * `targets` replaces the whole set when present. `copy_from` (POST only)
 * copies objective, targets and steps from another strategy — never results,
 * updates or links; explicit fields in the body win over copied ones. */
export interface StrategyInput {
  channel_id?: string | null;
  name: string;
  status?: StrategyStatus; // default 'active'
  objective?: string | null;
  idea_note?: string | null;
  effort?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  budget_cents?: number | null;
  budget_period?: BudgetPeriod | null;
  review_every?: ReviewCadence | null;
  next_review_date?: string | null;
  match_keys?: string[];
  outreach_vertical?: OutreachVertical | null;
  targets?: { metric: TargetMetric; value: number }[];
  copy_from?: string;
}

// Other endpoints (all under /api/admin/marketing, JSON, 400 { error } on bad input, 404 when missing):
//   DELETE /channels/:id                 soft delete; 409 if it has non-deleted strategies
//   DELETE /strategies/:id               soft delete (detaches nothing; leads keep strategy_id)
//   POST   /strategies/:id/review        body { note?: string } → writes a 'review' update dated today
//                                        and advances next_review_date by review_every from today
//                                        (1m default). Returns MarketingStrategyResponse.
//   POST   /strategies/:id/steps         { text, due_date? }           → MarketingStep
//   PATCH  /steps/:id                    { text?, due_date?, done?: boolean, sort_order? }
//   DELETE /steps/:id                    hard delete
//   POST   /strategies/:id/updates       { text, kind, date? (default today) } → MarketingUpdate
//   DELETE /updates/:id                  hard delete
//   POST   /strategies/:id/links         { label, kind, url (http/https only), social_post_id? } → MarketingLink
//   PATCH  /links/:id, DELETE /links/:id
//   PUT    /strategies/:id/metrics/:month  { values: Record<field_key, number | null> } — upserts;
//                                        null deletes that field; keys must be the channel's
//                                        manual_fields keys or SPEND_FIELD_KEY.
//
// Leads (existing /api/admin/leads/:id PATCH) gains `strategy_id: string | null`:
// setting it stores attribution 'manual' (null clears both). When the lead's
// acquisition was not set by hand (tracked in leads.acquisition_manual), it
// follows the strategy channel's acquisition. GET /leads/:id returns
// strategy_id, attribution, strategy_name, strategy_channel_name.

// Meta API contracts contain status only, never the access token.
export type MetaAdsErrorCode = 'not_configured' | 'sync_in_progress' | 'provider_unavailable' | 'rate_limited' | 'provider_access' | 'invalid_request' | 'invalid_payload' | 'content_too_large';
export interface MetaSyncResult {
  ok: boolean;
  at: number;
  code?: MetaAdsErrorCode;
  ad_sets?: number;
  rows?: number;
}
export interface MetaAdsStatus {
  configured: boolean;
  token_present: boolean;
  account_id: string | null;
  last_sync_at: number | null;
  last_result: MetaSyncResult | null;
}
export interface MetaAdSet {
  id: string;
  name: string;
  campaign_id: string | null;
  campaign_name: string | null;
  status: string | null;
  last_seen_at: number;
  created_at: number;
  updated_at: number;
}
export interface MetaInsightDaily {
  ad_set_id: string;
  date: string;
  spend_cents: number;
  impressions: number;
  reach: number;
  clicks: number;
  meta_leads: number;
  updated_at: number;
}
export interface MetaMetrics {
  spend_cents: number;
  impressions: number;
  reach: number;
  clicks: number;
  meta_leads: number;
  ctr: number | null;
}
export interface StrategyTotals extends MarketingTotals {
  meta: MetaMetrics | null;
}
export interface UnmappedAdSet {
  id: string;
  name: string;
  campaign_name: string | null;
  spend_cents: number;
  impressions: number;
  clicks: number;
}
export interface MarketingMetaResponse {
  status: MetaAdsStatus;
  nightly_enabled: boolean;
  ad_sets: (Pick<MetaAdSet, 'id' | 'name' | 'campaign_name' | 'status' | 'last_seen_at'> & {
    spend_30d_cents: number;
    spend_7d_cents: number;
    strategy_id: string | null;
    strategy_name: string | null;
  })[];
  strategies: { id: string; name: string; channel_name: string | null }[];
}
